import React, { useCallback, useEffect, useState } from 'react';
import {
  Heading,
  Chips,
  Flex,
  Text,
  Button,
} from '@vibe/core';
import { useMondayContext } from '../context/MondayContext';
import SchedulerBoard from '../components/scheduler/SchedulerBoard';
import { fetchBoardsByIds } from '../api/monday.api';
import MultiBoardPicker from '../components/shared/MultiBoardPicker';
import type { PickedBoard } from '../components/shared/BoardPicker';

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * Dashboard Widget surface entry point.
 *
 * Rendered inside a monday.com dashboard widget iframe. Has access to
 * multiple boardIds, allowing a unified resource view across boards.
 */
const DashboardWidget: React.FC = () => {
  const { context, isReady } = useMondayContext();
  const [selectedBoards, setSelectedBoards] = useState<PickedBoard[]>([]);

  // Auto-populate from context.boardIds by fetching their names
  useEffect(() => {
    if (context.boardIds && context.boardIds.length > 0 && selectedBoards.length === 0) {
      fetchBoardsByIds(context.boardIds.map(String))
        .then((boards) => {
          setSelectedBoards(boards);
        })
        .catch((err) => console.error('[DashboardWidget] Failed to fetch context boards:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.boardIds]);

  const handleBoardsChange = useCallback(
    (boards: PickedBoard[]) => {
      setSelectedBoards(boards);
    },
    [],
  );

  if (!isReady) {
    return (
      <div className="rp-view-loading">
        Loading dashboard context...
      </div>
    );
  }

  const activeBoardIds = selectedBoards.length > 0
    ? selectedBoards.map((b) => Number(b.id))
    : context.boardIds ?? [];

  if (activeBoardIds.length === 0) {
    return (
      <div className="rp-view-empty">
        <Heading type={Heading.types.H3}>Resource Planning</Heading>
        <Text type={Text.types.TEXT1} style={{ marginBottom: '16px' }}>
          Connect one or more boards to this widget to see a unified resource
          schedule.
        </Text>
        <MultiBoardPicker
          value={selectedBoards}
          onChange={handleBoardsChange}
          placeholder="Search and select boards..."
        />
      </div>
    );
  }

  return (
    <div className="rp-dashboard-widget">
      {/* Multi-board header showing connected boards */}
      <div className="rp-dashboard-widget__header">
        <Flex
          justify={Flex.justify.SPACE_BETWEEN}
          align={Flex.align.CENTER}
          className="rp-dashboard-widget__header-row"
        >
          <Flex gap={Flex.gaps.SMALL} align={Flex.align.CENTER}>
            <Heading type={Heading.types.H3}>
              Resource Planning
            </Heading>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {activeBoardIds.length} board{activeBoardIds.length > 1 ? 's' : ''} connected
            </Text>
          </Flex>
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={() => setSelectedBoards([])}
          >
            Change boards
          </Button>
        </Flex>

        {/* Board chips showing connected board names */}
        <Flex gap={Flex.gaps.XS} className="rp-dashboard-widget__board-chips">
          {selectedBoards.map((board) => (
            <Chips
              key={board.id}
              label={board.name}
              onDelete={() => setSelectedBoards((prev) => prev.filter((b) => b.id !== board.id))}
              color={Chips.colors.PRIMARY}
            />
          ))}
        </Flex>
      </div>

      {/*
        The SchedulerBoard component accepts multiple boardIds for
        the dashboard widget use case. We pass the first board as primary
        and all boards via multiBoardIds.
      */}
      <SchedulerBoard
        boardId={activeBoardIds[0]}
        multiBoardIds={activeBoardIds}
      />
    </div>
  );
};

export default DashboardWidget;
